/**
 * FolderPicker — Tree-view folder picker with checkboxes
 *
 * Used by the Connect Source wizard to let users select which remote folders to sync.
 * Renders a recursive tree with expand/collapse, checkboxes, file count, and size.
 */

import { useState } from 'react';
import { ChevronRight, Folder, FolderOpen } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { RemoteFolderNode } from '@/types/library';

interface FolderPickerProps {
  tree: RemoteFolderNode[];
  selected: Set<string>;
  onToggle: (folderId: string, path: string) => void;
  className?: string;
}

export function FolderPicker({ tree, selected, onToggle, className }: FolderPickerProps) {
  return (
    <div className={cn('border rounded-lg overflow-auto max-h-[300px]', className)}>
      {tree.length === 0 ? (
        <div className="p-6 text-center text-xs text-muted-foreground">No folders found</div>
      ) : (
        <div className="p-2">
          {tree.map(node => (
            <FolderNode key={node.id} node={node} depth={0} selected={selected} onToggle={onToggle} />
          ))}
        </div>
      )}
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes === 0) return '';
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1073741824).toFixed(1)} GB`;
}

function FolderNode({
  node,
  depth,
  selected,
  onToggle,
}: {
  node: RemoteFolderNode;
  depth: number;
  selected: Set<string>;
  onToggle: (folderId: string, path: string) => void;
}) {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = node.children.length > 0;
  const isSelected = selected.has(node.id);

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-1.5 py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors cursor-pointer group',
          isSelected && 'bg-primary/5',
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        {/* Expand/collapse arrow */}
        <button
          className={cn('h-4 w-4 shrink-0 flex items-center justify-center', !hasChildren && 'invisible')}
          onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}
        >
          <ChevronRight className={cn('h-3 w-3 text-muted-foreground transition-transform', expanded && 'rotate-90')} />
        </button>

        {/* Checkbox */}
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggle(node.id, node.path)}
          className="shrink-0"
        />

        {/* Folder icon */}
        {expanded && hasChildren ? (
          <FolderOpen className="h-4 w-4 text-amber-500 dark:text-amber-400 shrink-0" />
        ) : (
          <Folder className="h-4 w-4 text-amber-500 dark:text-amber-400 shrink-0" />
        )}

        {/* Name + metadata */}
        <span className="text-xs font-medium flex-1 truncate" onClick={() => { if (hasChildren) setExpanded(!expanded); }}>
          {node.name}
        </span>
        {node.fileCount > 0 && (
          <span className="text-[10px] text-muted-foreground shrink-0">{node.fileCount} files</span>
        )}
        {node.sizeBytes > 0 && (
          <span className="text-[10px] text-muted-foreground/60 shrink-0">{formatSize(node.sizeBytes)}</span>
        )}
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div>
          {node.children.map(child => (
            <FolderNode key={child.id} node={child} depth={depth + 1} selected={selected} onToggle={onToggle} />
          ))}
        </div>
      )}
    </div>
  );
}
